import { type Level } from '../models/level';
import { bakeCityLayer } from './backgrounds';
import { gearfallMap } from './geography';
import { currentLook } from './look';
import {
  bakePaintedLayer,
  fitPaintedBoard,
  paintedBoard,
  type PaintedView,
  requestPaintedBoard,
} from './paintedBoard';
import { planCity, type Landmark, type Point } from './cityPlan';
import {
  ensureSpritesLoaded,
  onSpritesLoaded,
  spriteCatalog,
  spriteRevision,
  type SpriteCatalog,
} from './sprites';
import { type MapTheme } from './theme';
import { fitViewport, insetKey, type Insets, type Viewport, uniformInsets } from './viewport';

export type MapScene = Pick<Level, 'id' | 'graph'> & {
  readonly sizeScale?: number;
  readonly canalPath?: readonly Point[] | null;
  readonly landmarksFor?: (catalog: SpriteCatalog) => readonly Landmark[];
};

export class CityMapRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private insets: Insets;
  private width = 0;
  private height = 0;
  private pixelRatio = 1;
  private scene: MapScene | null = null;
  private cityLayer: HTMLCanvasElement | null = null;
  private cityKey = '';
  private lastPaint: { scene: MapScene; theme: MapTheme } | null = null;
  private readonly stopWatchingSprites: () => void;
  private readonly onRepaint: (() => void) | undefined;
  private readonly requested = new Set<string>();

  constructor(canvas: HTMLCanvasElement, inset: number | Insets, onRepaint?: () => void) {
    this.canvas = canvas;
    this.onRepaint = onRepaint;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.insets = typeof inset === 'number' ? uniformInsets(inset) : inset;
    this.stopWatchingSprites =
      spriteRevision() > 0
        ? () => {}
        : onSpritesLoaded(() => {
            this.stopWatchingSprites();
            this.cityKey = '';
            const last = this.lastPaint;
            if (last) this.render(last.scene, last.theme);
            onRepaint?.();
          });
    void ensureSpritesLoaded();
  }

  dispose(): void {
    this.stopWatchingSprites();
    this.cityLayer = null;
    this.lastPaint = null;
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.width = rect.width;
    this.height = rect.height;
    this.pixelRatio = dpr;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  setInsets(insets: Insets): void {
    this.insets = insets;
  }

  private get painted(): PaintedView | null {
    const board = this.scene && currentLook() === 'diorama' ? paintedBoard(this.scene.id) : null;
    if (!board || !this.scene) return null;
    const fill = this.scene.id === gearfallMap().id ? 'cover' : 'contain';
    return fitPaintedBoard(board, this.width, this.height, this.insets, fill);
  }

  private get view(): Viewport {
    return (
      this.painted ?? fitViewport(this.scene?.graph ?? null, this.width, this.height, this.insets)
    );
  }

  private requestPainting(scene: MapScene): void {
    if (currentLook() !== 'diorama' || this.requested.has(scene.id)) return;
    this.requested.add(scene.id);
    void requestPaintedBoard(scene.id).then((board) => {
      if (!board || this.scene?.id !== scene.id) return;
      this.cityKey = '';
      const last = this.lastPaint;
      if (last) this.render(last.scene, last.theme);
      this.onRepaint?.();
    });
  }

  project(position: Point): Point {
    return this.view.project(position);
  }

  get size(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  render(scene: MapScene, theme: MapTheme): void {
    this.scene = scene;
    this.lastPaint = { scene, theme };
    this.requestPainting(scene);
    if (!(this.view.scale > 0)) return;

    const { ctx } = this;
    ctx.fillStyle = theme.paper;
    ctx.fillRect(0, 0, this.width, this.height);

    const painted = this.painted;
    const board = painted ? paintedBoard(scene.id) : null;
    const key = `${painted ? 'painted' : 'city'}|${scene.id}|${theme.id}|${this.width}x${this.height}@${this.pixelRatio}|${insetKey(this.insets)}|${spriteRevision()}`;
    if (key !== this.cityKey && painted && board) {
      this.cityLayer = bakePaintedLayer(board, painted, this.width, this.height, this.pixelRatio);
      this.cityKey = key;
    } else if (key !== this.cityKey) {
      const view = this.view;
      const catalog = spriteCatalog();
      this.cityLayer = bakeCityLayer(
        planCity(scene, {
          scene: view.scene,
          catalog,
          ...(scene.sizeScale === undefined ? {} : { sizeScale: scene.sizeScale }),
          ...(scene.canalPath === undefined ? {} : { canalPath: scene.canalPath }),
          ...(scene.landmarksFor ? { landmarks: scene.landmarksFor(catalog) } : {}),
        }),
        theme,
        view.projection,
        this.width,
        this.height,
        this.pixelRatio,
      );
      this.cityKey = key;
    }
    if (this.cityLayer) ctx.drawImage(this.cityLayer, 0, 0, this.width, this.height);
  }
}
